import { useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { type: "generico", name: "", company: "", address: "", city: "", cap: "", province: "", phone: "" };

export default function AddressBook({ addresses, token, onUpdate }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (addr) => {
    setForm({
      type: addr.type || "generico", name: addr.name, company: addr.company || "",
      address: addr.address, city: addr.city, cap: addr.cap,
      province: addr.province || "", phone: addr.phone || "",
    });
    setEditing(addr.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.city || !form.cap) {
      toast.error("Completa i campi obbligatori (nome, indirizzo, città, CAP)");
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/addresses/${editing}`, form, { headers });
        toast.success("Indirizzo aggiornato");
      } else {
        await axios.post(`${API}/addresses`, form, { headers });
        toast.success("Indirizzo salvato");
      }
      resetForm();
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo indirizzo?")) return;
    try {
      await axios.delete(`${API}/addresses/${id}`, { headers });
      toast.success("Indirizzo eliminato");
      onUpdate();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const AddressCard = ({ addr }) => (
    <div className="border-2 border-zinc-200 p-4 hover:border-zinc-300 transition-colors" data-testid={`address-card-${addr.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#09090B] text-sm">{addr.name}</p>
          <p className="text-xs text-zinc-500 mt-1">{addr.address}</p>
          <p className="text-xs text-zinc-500">{addr.cap} {addr.city} {addr.province && `(${addr.province})`}</p>
          {addr.phone && <p className="text-xs text-zinc-400 mt-1">Tel: {addr.phone}</p>}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button onClick={() => handleEdit(addr)} className="p-1.5 text-zinc-400 hover:text-[#FF5200] transition-colors" data-testid={`edit-address-${addr.id}`}>
            <Pencil size={14} />
          </button>
          <button onClick={() => handleDelete(addr.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors" data-testid={`delete-address-${addr.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="address-book">
      {/* Add button */}
      <div className="flex justify-end mb-6">
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors font-heading"
          data-testid="add-address-btn"
        >
          <Plus size={16} /> Nuovo Indirizzo
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border-2 border-[#FF5200] bg-[#FFF7ED] p-6 mb-8" data-testid="address-form">
          <div className="flex items-center justify-between mb-4">
            <p className="font-heading text-lg font-bold uppercase text-[#09090B]">
              {editing ? "Modifica Indirizzo" : "Nuovo Indirizzo"}
            </p>
            <button onClick={resetForm} className="p-1 text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Nome / Ragione sociale *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="Nome e cognome o ragione sociale"
                data-testid="address-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Indirizzo *</label>
              <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="Via/Piazza, numero civico"
                data-testid="address-street-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Città *</label>
                <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="Città"
                  data-testid="address-city-input" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">CAP *</label>
                <input type="text" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="00000"
                  data-testid="address-cap-input" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Provincia</label>
                <input type="text" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}
                  className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="XX"
                  data-testid="address-province-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Telefono</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body" placeholder="+39 XXX XXX XXXX"
                data-testid="address-phone-input" />
            </div>
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-6 py-3 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors font-heading disabled:opacity-50"
              data-testid="save-address-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {editing ? "Aggiorna" : "Salva"}
            </button>
          </form>
        </div>
      )}

      {/* Address List */}
      {addresses.length === 0 && !showForm ? (
        <div className="text-center py-16 text-zinc-400 font-body" data-testid="no-addresses">
          <MapPin size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg">Nessun indirizzo salvato.</p>
          <p className="text-sm mt-1">Aggiungi i tuoi indirizzi frequenti per velocizzare la creazione delle spedizioni.</p>
        </div>
      ) : (
        <div>
          <p className="text-xs font-bold tracking-widest uppercase text-zinc-400 mb-3 font-heading">Indirizzi salvati ({addresses.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {addresses.map((a) => <AddressCard key={a.id} addr={a} />)}
          </div>
        </div>
      )}
    </div>
  );
}
